import React, {ChangeEvent, JSX, useState} from "react";
import {InputOnChangeData, RadioGroupOnChangeData} from "@fluentui/react-components";
import ErrorDialog, {ErrorDialogProps} from "../ui/admin/common/ErrorDialog";

export type StatePair<T> = {
    value: T
    setValue: (value: T) => T,
    reset(): void
}

export type BooleanStatePair = StatePair<boolean> & {
    setTrue(): void
    setFalse(): void
    toggle(): void
}

export type InputStringStatePair = StatePair<string> & {
    onChange(event: ChangeEvent<HTMLInputElement>, data: InputOnChangeData): void
}

export type UndefinedStatePair<T> = StatePair<T | undefined> & {
    setDefinedValue: (value: T) => T
    unset(): void
}

export type ErrorStatePair<T extends Error> = UndefinedStatePair<T> & {
    print(): void
    alert(): void
    openDialog(): void
    closeDialog(): void
    setDialogOpened(isDialogOpened: boolean): void
    Dialog(props: ErrorDialogProps<T>): JSX.Element
}

export function useStatePair<T>(initialValue: T): StatePair<T> {
    const [value, setValue] = useState<T>(initialValue)
    return {
        value: value,
        setValue(value: T): T {
            setValue(value)

            return value
        },
        reset() {
            setValue(initialValue)
        }
    }
}

export function useBoolean(initialValue: boolean): BooleanStatePair {
    const [value, setValue] = useState<boolean>(initialValue)
    return {
        value: value,
        setValue(value: boolean): boolean {
            setValue(value)

            return value
        },
        reset() {
            setValue(initialValue)
        },
        setTrue() {
            setValue(true)
        },
        setFalse() {
            setValue(false)
        },
        toggle() {
            setValue(!value)
        }
    }
}

export function useInputString(initialValue: string): InputStringStatePair {
    const statePair = useStatePair<string>(initialValue)
    return {
        ...statePair,
        onChange(event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) {
            statePair.setValue(data.value)
        }
    }
}

export function useRadioString(initialValue: string): InputStringStatePair {
    const statePair = useStatePair<string>(initialValue)
    return {
        ...statePair,
        onChange(event: React.FormEvent<HTMLDivElement>, data: RadioGroupOnChangeData) {
            statePair.setValue(data.value)
        }
    }
}


export function useUndefined<T>(initialValue?: T): UndefinedStatePair<T> {
    const [value, setValue] = useState<T | undefined>(initialValue)
    return {
        value: value,
        setValue(value: T | undefined): T | undefined {
            setValue(value)

            return value
        },
        setDefinedValue(value: T): T {
            setValue(value)

            return value
        },
        reset() {
            setValue(initialValue)
        },
        unset() {
            setValue(undefined)
        }
    }
}

export function useError<T extends Error>(initialValue?: T): ErrorStatePair<T> {
    const [value, setValue] = useState<T | undefined>(initialValue)
    const isDialogOpenedState = useBoolean(false)
    return {
        value: value,
        setValue(value: T | undefined): T | undefined {
            setValue(value)
            if (value !== undefined) {
                console.error(value)
                isDialogOpenedState.setTrue()
            }

            return value
        },
        setDefinedValue(value: T): T {
            setValue(value)
            if (value !== undefined) {
                console.error(value)
                isDialogOpenedState.setTrue()
            }

            return value
        },
        reset() {
            setValue(initialValue)
        },
        unset() {
            setValue(undefined)
        },
        print() {
            console.error(value)
        },
        alert() {
            window.alert(value)
        },
        openDialog() {
            isDialogOpenedState.setTrue()
        },
        closeDialog() {
            isDialogOpenedState.setFalse()
        },
        setDialogOpened(isDialogOpened: boolean) {
            isDialogOpenedState.setValue(isDialogOpened)
        },
        Dialog(props: ErrorDialogProps<T>): React.JSX.Element {
            return <>
                <ErrorDialog
                    open={isDialogOpenedState.value}
                    onOpenChange={(event, data) => isDialogOpenedState.setValue(data.open)}
                    error={value}
                    {...props} />
            </>
        }
    }
}